/**
 * Reach-resolved session values a tool input can be filled from instead of asking the agent, each
 * templatable as `{{context.<key>}}` and filled at request time:
 *   - tenantExternalId: the tenant's ID in the partner's own system (`businesses.external_id`).
 *   - participantPhone: the conversation participant's phone (E.164), from the agent session's
 *     `participant_id` on phone-based channels (SMS today).
 *
 * This list is the single source of truth for them: the MCP session type, the templatable
 * allowlist, the missing-context messages (backend), and the dashboard Source options all key off
 * it, so adding a value is one entry here — the compiler then flags whatever still needs a message
 * or a label.
 */
export const FILLABLE_CONTEXT_KEYS = ['tenantExternalId', 'participantPhone'];
/** Identity keys the MCP server derives from the verified token/business — templatable, never producer-supplied. */
const TRUSTED_CONTEXT_KEYS = [
    'scope',
    'partnerId',
    'businessId',
    'partnerUserId',
    'agentId',
    'sessionId',
];
/**
 * Session keys a template may reference as `{{context.<key>}}` — trusted identity plus the
 * Reach-fillable values. mcp-core pins every entry to a real `McpSessionContext` field via
 * `satisfies`, so the list can't drift from the session type.
 */
export const TEMPLATABLE_CONTEXT_KEYS = [...TRUSTED_CONTEXT_KEYS, ...FILLABLE_CONTEXT_KEYS];
/** `{{...}}` interpolation blocks in a template string. */
export const INTERP_RE = /\{\{([\s\S]*?)\}\}/g;
/** `{{args.<name>}}` references in a template string. */
export const ARG_REF_RE = /\bargs\.([\w-]+)/g;
/** `{{context.<key>}}` references in a template string. */
export const CONTEXT_REF_RE = /\bcontext\.(\w+)/g;
/** `{{secrets.<slot>}}` references in a template string. */
export const SECRET_REF_RE = /\bsecrets\.(\w+)/g;
/** `{{variables.<name>}}` references in a template string. */
export const VARIABLE_REF_RE = /\bvariables\.(\w+)/g;
/** Recursively collects every string in a template tree (objects, arrays, strings). */
export function collectTemplateStrings(node) {
    if (typeof node === 'string')
        return [node];
    if (Array.isArray(node))
        return node.flatMap(collectTemplateStrings);
    if (node && typeof node === 'object') {
        return Object.values(node).flatMap(collectTemplateStrings);
    }
    return [];
}
/**
 * Names referenced via the given namespace regex inside `{{...}}` expressions. Text outside an
 * interpolation is literal, never a reference.
 */
export function extractReferences(template, re) {
    const out = new Set();
    for (const [, expr] of template.matchAll(INTERP_RE)) {
        for (const [, name] of expr.matchAll(re)) {
            if (name)
                out.add(name);
        }
    }
    return out;
}
/**
 * Schema properties the request mapping fills from `{{context.*}}` instead of the agent: the
 * property name is keyed in the mapping's body/query with a value that references context and
 * never `args`. A property whose `{{args.<name>}}` is referenced anywhere in the mapping is
 * always agent-supplied, no matter how it's keyed.
 */
export function contextSourcedFieldNames(inputs) {
    const { requestSchema, requestMapping } = inputs;
    const properties = (requestSchema?.properties ?? {});
    const mapping = (requestMapping ?? {});
    const argReferencedNames = new Set();
    for (const template of collectTemplateStrings(mapping)) {
        for (const name of extractReferences(template, ARG_REF_RE)) {
            argReferencedNames.add(name);
        }
    }
    const result = new Set();
    for (const location of ['body', 'query']) {
        const block = mapping[location];
        if (!block || typeof block !== 'object')
            continue;
        for (const [name, value] of Object.entries(block)) {
            if (!(name in properties) || argReferencedNames.has(name))
                continue;
            if (typeof value === 'string' && extractReferences(value, CONTEXT_REF_RE).size > 0) {
                result.add(name);
            }
        }
    }
    return result;
}
/**
 * The agent-facing argument schema: `request_schema` minus context-sourced properties
 * ({@link contextSourcedFieldNames}). `request_schema` describes the outbound partner request —
 * context-sourced fields are required on the wire but are Reach-resolved at request time, so
 * every surface an LLM sees (MCP registration, execution arg validation, dashboard args example)
 * must derive through this instead of reading `request_schema` raw. Legacy rows whose stored
 * `required` still lists context-sourced fields are handled here, at read time.
 */
export function deriveAgentArgsSchema(inputs) {
    const schema = inputs.requestSchema ?? {};
    const contextSourced = contextSourcedFieldNames(inputs);
    if (contextSourced.size === 0)
        return schema;
    const properties = Object.fromEntries(Object.entries((schema.properties ?? {})).filter(([name]) => !contextSourced.has(name)));
    const required = Array.isArray(schema.required)
        ? schema.required.filter(name => !contextSourced.has(name))
        : schema.required;
    return {
        ...schema,
        properties,
        ...(required !== undefined ? { required } : {}),
    };
}
//# sourceMappingURL=mcp-context.js.map